import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Coins, Sparkles, Globe, Check, ArrowRight, Tv, Layers, ShoppingCart,
  Gift, Users, Award,
} from 'lucide-react';
import { AssistedDeploymentModal } from '../components/AssistedDeploymentModal';
import { useCart } from '../contexts/CartContext';

const SKU = 'coin-flip-machine';
const FULL_NAME = 'Coin Flip Machine';
const PRICE = 49;
const WIDGET_FAMILY = 'coin-flip';

const USE_CASES = [
  'Random team decisions',
  'Tie breakers',
  'Buyer choice decisions',
  'Bonus spot decisions',
  'Draft order decisions',
  'Giveaway decisions',
  'Any heads-or-tails style random result',
];

const BENEFITS = [
  { icon: Sparkles, title: 'Adds excitement', text: 'Turns "I dunno, flip for it" into a real moment on stream.' },
  { icon: Award, title: 'Looks pro on camera', text: 'Way cleaner than fishing a physical coin out of your pocket mid-break.' },
  { icon: Users, title: 'Transparent for buyers', text: 'Random decisions feel fair when everyone watches the same flip.' },
  { icon: Tv, title: 'OBS / browser-source ready', text: 'Drop the URL into OBS, Streamlabs, or just pop it open in a tab.' },
];

const INCLUDED = [
  'Web-hosted Coin Flip Machine URL',
  'No LAB21G branding',
  'As-is Coin Flip Machine design',
  'OBS / browser source compatibility',
  'Simple live-stream-ready layout',
];

export const CoinFlipMachine = () => {
  const [pendingItem, setPendingItem] = useState(null);
  const [replacingLineId, setReplacingLineId] = useState(null);
  const { items } = useCart();

  const startAdd = () => {
    const existing = items.find((i) => i.widgetFamily === WIDGET_FAMILY);
    if (existing) {
      toast.info('Already in your cart', {
        description: `${FULL_NAME} is already in your cart.`,
        duration: 2400,
      });
      return;
    }
    setReplacingLineId(null);
    setPendingItem({
      sku: SKU,
      name: FULL_NAME,
      price: PRICE,
      widgetFamily: WIDGET_FAMILY,
      defaultAssisted: false,
      replacingName: null,
    });
  };

  const scrollToPricing = () => {
    document.querySelector('[data-testid="coin-flip-pricing"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-testid="coin-flip-machine-page">
      <AssistedDeploymentModal pendingItem={pendingItem} replacingLineId={replacingLineId} onClose={() => { setPendingItem(null); setReplacingLineId(null); }} />

      {/* HERO */}
      <section className="hero-gradient py-20 px-4 sm:px-6 lg:px-8 border-b border-subtle">
        <div className="max-w-5xl mx-auto text-center">
          <span className="eyebrow eyebrow-blue" data-testid="coin-flip-eyebrow">Stream Widget · Coin Flip Machine</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 mb-5 tracking-tight leading-[1.05]" data-testid="coin-flip-headline">
            <span className="text-strong">Coin Flip </span>
            <span className="text-gradient-brand">Machine</span>
          </h1>
          <p className="text-base sm:text-lg text-muted max-w-2xl mx-auto mb-9">
            A simple, professional, web-hosted coin flip tool for live stream sellers,
            sports card breakers, and anyone who needs a clean 50/50 on camera.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button type="button" onClick={scrollToPricing} className="btn-primary px-6 py-3 inline-flex items-center space-x-2" data-testid="coin-flip-cta-get">
              <span>Get Coin Flip Machine</span><ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/#widgets" className="btn-secondary px-6 py-3 inline-flex items-center space-x-2" data-testid="coin-flip-cta-options">
              <Layers className="w-4 h-4" /><span>View Widget Options</span>
            </Link>
          </div>
          <div className="mt-7 text-xs text-faded inline-flex items-center gap-2 flex-wrap justify-center">
            <span className="px-2.5 py-1 rounded border border-default">Web-hosted</span>
            <span className="px-2.5 py-1 rounded border border-default">OBS · Streamlabs</span>
            <span className="px-2.5 py-1 rounded border border-default">Browser source ready</span>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="coin-flip-use-cases">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="eyebrow">Where it shines</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-strong tracking-tight">
              50/50 calls, on camera, in seconds.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-testid="coin-flip-use-list">
            {USE_CASES.map((u) => (
              <div key={u} className="card-surface p-4 flex items-center space-x-3">
                <Coins className="w-4 h-4 accent-green shrink-0" />
                <span className="text-sm text-default">{u}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 border-y border-subtle" data-testid="coin-flip-benefits">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="eyebrow eyebrow-blue">Why sellers use it</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-strong tracking-tight">
              Looks better than digging for a quarter.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {BENEFITS.map(({ icon: Icon, title, text }, i) => (
              <div key={title} className="card-surface p-6 flex space-x-4" data-testid={`coin-flip-benefit-${i}`}>
                <div className="shrink-0"><Icon className="w-6 h-6 accent-green" /></div>
                <div>
                  <h3 className="text-base font-semibold text-strong mb-1">{title}</h3>
                  <p className="text-sm text-muted leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-testid="coin-flip-pricing">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <span className="eyebrow">Pricing</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-strong tracking-tight">
              One simple price.
            </h2>
            <p className="text-sm text-muted mt-3">No tiers, no add-ons, no fluff.</p>
          </div>
          <div className="card-surface p-8 border-emphasis" style={{ boxShadow: '0 0 30px -8px rgba(57, 255, 20, 0.25)' }} data-testid="coin-flip-tier">
            <h3 className="text-lg font-semibold text-strong mb-1">Coin Flip Machine</h3>
            <p className="text-xs text-muted mb-6">A clean, ready-to-use coin flip for live streams.</p>
            <div className="mb-6">
              <span className="text-5xl font-bold text-strong" data-testid="coin-flip-price">${PRICE}</span>
              <span className="text-sm text-faded ml-2">one-time</span>
            </div>
            <ul className="space-y-2.5 mb-6">
              {INCLUDED.map((f) => (
                <li key={f} className="flex items-start space-x-2 text-sm text-default">
                  <Check className="w-4 h-4 mt-0.5 shrink-0 accent-green" /><span>{f}</span>
                </li>
              ))}
            </ul>
            <button type="button" onClick={startAdd} className="block w-full btn-primary px-5 py-3 text-sm font-semibold inline-flex items-center justify-center space-x-2" data-testid="coin-flip-cta-buy">
              <ShoppingCart className="w-4 h-4" />
              <span>Get this option</span>
            </button>
            <p className="text-xs text-faded mt-4 text-center">
              This product comes as-is. Customization is not available for this widget.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-y border-subtle" data-testid="coin-flip-how">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="eyebrow eyebrow-blue">How it works</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 text-strong tracking-tight">
              Live in four quick steps.
            </h2>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="coin-flip-steps">
            {[
              'Customer purchases the Coin Flip Machine.',
              'LAB21G provides a web-hosted Coin Flip Machine URL.',
              'Customer adds the URL to OBS, Streamlabs, or opens it in a browser.',
              'Customer uses it live whenever a coin flip decision is needed.',
            ].map((step, i) => (
              <li key={step} className="card-surface p-5 flex space-x-4">
                <span className="brand-green text-base font-bold" style={{ color: '#39FF14' }}>0{i + 1}</span>
                <span className="text-sm text-default leading-relaxed">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 hero-gradient" data-testid="coin-flip-final-cta">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-strong tracking-tight">
            Need a clean coin flip tool for your LIVE stream?
          </h2>
          <p className="text-base text-muted mb-8">
            Let LAB21G provide your web-hosted Coin Flip Machine and give your stream
            a fun, professional way to handle 50/50 decisions.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
            <button type="button" onClick={startAdd} className="btn-primary px-6 py-3 inline-flex items-center space-x-2" data-testid="coin-flip-final-buy">
              <ShoppingCart className="w-4 h-4" />
              <span>Get Coin Flip Machine</span>
            </button>
            <Link to="/#widgets" className="btn-secondary px-6 py-3 inline-flex items-center space-x-2" data-testid="coin-flip-final-options">
              <Layers className="w-4 h-4" /><span>View Widget Options</span>
            </Link>
            <Link to="/" className="btn-secondary px-6 py-3 inline-flex items-center space-x-2" data-testid="coin-flip-final-back">
              <span>Back to all widgets</span>
            </Link>
          </div>
          <p className="text-sm text-faded tracking-wide">
            <span className="text-strong">Flip it live.</span> Keep it <span className="text-gradient-brand font-semibold">legendary</span>.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CoinFlipMachine;
